import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Notes from "./header";
import FooterManual from "./footerManual";
import { useParams, useNavigate } from "react-router-dom";

// Configure axios instance
const api = axios.create({
  baseURL: "https://car.amar.co.jp/api",
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Accept"] = "application/json";
    return config;
  },
  (error) => Promise.reject(error)
);

const InquiryTickets = ({ authToken }) => {
  const { shippingCalculationId } = useParams();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showReplyChat, setShowReplyChat] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isDownloading, setIsDownloading] = useState({});
  const [messageStatuses, setMessageStatuses] = useState({});
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const getAuthToken = () => {
    return authToken || localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  };

  const transformTicketData = (apiTicket) => {
    return {
      id: apiTicket.ticketId,
      ticketNumber: apiTicket.ticketNumber,
      subject: apiTicket.subject,
      description: apiTicket.message,
      type: mapCategoryToType(apiTicket.category),
      status: "open",
      date: convertDate(apiTicket.creationDate),
      priority: apiTicket.priority.toLowerCase(),
      userName: `${apiTicket.user.firstName} ${apiTicket.user.lastName}`,
      userEmail: apiTicket.user.email,
      carModel: apiTicket.carModel.carModelName,
      category: apiTicket.category,
      attachmentPath: apiTicket.attachmentPath,
      attachmentFileName: apiTicket.attachmentFileName,
      replies: 0,
      assignedTo: "Unassigned",
      isRead: apiTicket.isRead,
    };
  };

  const mapCategoryToType = (category) => {
    const categoryMap = {
      GENERAL_INQUIRY: "inquiry",
      TECHNICAL_ISSUE: "technical",
      BILLING_QUESTION: "billing",
      FEEDBACK_SUGGESTION: "feedback",
      ORDER_ISSUE: "order",
    };
    return categoryMap[category] || "inquiry";
  };

  const convertDate = (dateString) => {
    try {
      const [date, time] = dateString.split(" ");
      const [day, month, year] = date.split("-");
      return `${year}-${month}-${day}`;
    } catch (e) {
      return new Date().toISOString().split("T")[0];
    }
  };

  useEffect(() => {
    if (shippingCalculationId) {
      fetchTicketsByInquiry();
    }

    const unreadInterval = setInterval(() => {
      tickets.forEach((ticket) => {
        fetchUnreadCount(ticket.ticketNumber);
      });
    }, 5000);

    return () => clearInterval(unreadInterval);
  }, [shippingCalculationId]);

  useEffect(() => {
    if (selectedTicket && chatMessages.length > 0) {
      fetchUnreadCount(selectedTicket.ticketNumber);
    }
  }, [chatMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  useEffect(() => {
    if (tickets.length > 0) {
      tickets.forEach((ticket) => {
        fetchUnreadCount(ticket.ticketNumber);
      });
    }
  }, [tickets.length]);

  // ---- KEY CHANGE: Fetch tickets by shippingCalculationId ----
  const fetchTicketsByInquiry = async () => {
    const token = getAuthToken();
    if (!token) {
      setError("Authentication token not found. Please login first.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `/dashboard/raise-ticket/get/ticket/by/${shippingCalculationId}`
      );

      if (response.data.success && response.data.tickets) {
        const transformedTickets = response.data.tickets.map(transformTicketData);
        setTickets(transformedTickets);

        transformedTickets.forEach((ticket) => {
          fetchUnreadCount(ticket.ticketNumber);
        });
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching inquiry tickets:", err);
      setError(err.message || "Failed to load tickets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async (ticketNumber) => {
    try {
      const response = await api.get(
        `/dashboard/inquiry-chat/unread-count/${ticketNumber}`
      );

      if (response.data.success) {
        setUnreadCounts((prev) => ({
          ...prev,
          [ticketNumber]: response.data.unreadCount,
        }));
      }
    } catch (err) {
      console.error("Error fetching unread count:", err);
    }
  };

  const fetchChatHistory = async (ticketNumber) => {
    setLoadingChat(true);
    setChatError(null);

    try {
      const response = await api.get(
        `/dashboard/inquiry-chat/history/${ticketNumber}`
      );

      setChatMessages(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching chat history:", err);
      setChatError(err.message || "Failed to load chat history.");
    } finally {
      setLoadingChat(false);
    }
  };

  const markMessagesAsRead = async (ticketNumber) => {
    try {
      await api.put(`/dashboard/inquiry-chat/mark-read/${ticketNumber}`);

      setUnreadCounts((prev) => ({
        ...prev,
        [ticketNumber]: 0,
      }));

      await fetchChatHistory(ticketNumber);
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const handleDownloadAttachment = async (chatId, fileName) => {
    try {
      setIsDownloading((prev) => ({ ...prev, [chatId]: true }));
      const token = getAuthToken();

      if (!token) {
        alert("Authentication token not found. Please login again.");
        setIsDownloading((prev) => ({ ...prev, [chatId]: false }));
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/inquiry-chat/download/${chatId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 404) {
        alert("No attachment found for this message.");
        setIsDownloading((prev) => ({ ...prev, [chatId]: false }));
        return;
      }

      if (!response.ok) {
        if (response.status === 401) {
          alert("Your session has expired. Please login again.");
        } else if (response.status === 403) {
          alert("You don't have permission to download this file.");
        } else {
          alert(`Download failed with status ${response.statusText}`);
        }
        setIsDownloading((prev) => ({ ...prev, [chatId]: false }));
        return;
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        alert("Downloaded file is empty.");
        setIsDownloading((prev) => ({ ...prev, [chatId]: false }));
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      setSuccessMessage(`${fileName} downloaded successfully!`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      setIsDownloading((prev) => ({ ...prev, [chatId]: false }));
    } catch (error) {
      console.error("Error downloading attachment:", error);
      setError(`Failed to download: ${error.message}`);
      setIsDownloading((prev) => ({ ...prev, [chatId]: false }));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      resolved: "#e8f5e9",
      "in-progress": "#e3f2fd",
      open: "#fff3e0",
      pending: "#ffebee",
    };
    return colors[status] || "#f5f5f5";
  };

  const getStatusTextColor = (status) => {
    const colors = {
      resolved: "#2e7d32",
      "in-progress": "#1976d2",
      open: "#e65100",
      pending: "#c62828",
    };
    return colors[status] || "#333";
  };

  const getPriorityColor = (priority) => {
    const p = priority?.toLowerCase() || "";
    if (p === "high" || p === "urgent") return "#f44336";
    if (p === "medium") return "#ff9800";
    if (p === "low") return "#4caf50";
    return "#666";
  };

  const getTypeText = (type) => {
    const typeMap = {
      order: "Order Issue",
      inquiry: "Inquiry",
      technical: "Technical",
      billing: "Billing",
      feedback: "Feedback",
    };
    return typeMap[type] || "Inquiry";
  };

  const handleCloseDetailsDialog = () => {
    setShowDetails(false);
    setSelectedTicket(null);
  };

  const handleOpenChat = async (ticket) => {
    setSelectedTicket(ticket);
    setShowReplyChat(true);
    setAttachedFile(null);
    setReplyMessage("");
    setChatMessages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    await fetchChatHistory(ticket.ticketNumber);
    await markMessagesAsRead(ticket.ticketNumber);
  };

  const handleCloseChat = () => {
    setShowReplyChat(false);
    setSelectedTicket(null);
    setChatMessages([]);
    setReplyMessage("");
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setTimeout(() => {
      tickets.forEach((ticket) => {
        fetchUnreadCount(ticket.ticketNumber);
      });
    }, 500);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setAttachedFile(file);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() && !attachedFile) return;

    setSendingMessage(true);

    const tempMessageId = `temp-${Date.now()}`;
    const tempMessage = {
      chatId: tempMessageId,
      message: replyMessage,
      senderType: "USER",
      senderFullName: "You",
      attachmentFileName: attachedFile?.name,
      formattedDateTime: new Date().toLocaleString(),
      isRead: false,
      status: "sent",
    };

    setChatMessages((prev) => [...prev, tempMessage]);
    setMessageStatuses((prev) => ({ ...prev, [tempMessageId]: "sent" }));

    try {
      if (!selectedTicket) {
        throw new Error("No ticket selected");
      }

      const formData = new FormData();

      if (attachedFile) {
        formData.append("attachment", attachedFile);
      } else {
        formData.append("attachment", new Blob(), "");
      }

      const response = await api.post("/dashboard/inquiry-chat/send", formData, {
        params: {
          ticketNumber: selectedTicket.ticketNumber,
          message: replyMessage,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setMessageStatuses((prev) => ({ ...prev, [tempMessageId]: "delivered" }));

        await fetchChatHistory(selectedTicket.ticketNumber);

        setReplyMessage("");
        setAttachedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        setSuccessMessage("Message sent successfully!");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        throw new Error(response.data.message || "Failed to send message");
      }
    } catch (err) {
      console.error("Error submitting reply:", err);
      setChatMessages((prev) => prev.filter((msg) => msg.chatId !== tempMessageId));
      setMessageStatuses((prev) => {
        const updated = { ...prev };
        delete updated[tempMessageId];
        return updated;
      });

      setError(err.message || "Failed to submit reply");
      setShowSuccess(false);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatChatTime = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    const options = { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
    return date.toLocaleDateString(undefined, options);
  };

  const getSenderColor = (senderType) => {
    if (senderType === "ADMIN") return "#1976d2";
    if (senderType === "SUPERADMIN") return "#d32f2f";
    if (senderType === "USER") return "#666";
    return "#666";
  };

  const getSenderBgColor = (senderType) => {
    if (senderType === "ADMIN") return "#e3f2fd";
    if (senderType === "SUPERADMIN") return "#ffebee";
    if (senderType === "USER") return "#e3f2fd";
    return "#f5f5f5";
  };

  const getMessageStatusIndicator = (msg) => {
    if (msg.senderType !== "USER") return null;

    if (msg.isRead === true) {
      return (
        <span style={{ color: "#1976d2", fontSize: "12px", fontWeight: "bold", title: "Message read" }}>
          ✓✓
        </span>
      );
    }

    if (msg.isRead === false || msg.isRead === undefined) {
      return (
        <span style={{ color: "#999", fontSize: "12px", fontWeight: "bold", title: "Message delivered" }}>
          ✓✓
        </span>
      );
    }

    return null;
  };

  const filteredTickets = tickets.filter((ticket) => {
    const matchStatus = filterStatus === "all" || ticket.status === filterStatus;
    const matchPriority =
      filterPriority === "all" ||
      ticket.priority.toLowerCase() === filterPriority.toLowerCase();
    const matchType = filterType === "all" || ticket.type === filterType;
    const matchSearch =
      searchText === "" ||
      ticket.ticketNumber.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.userName.toLowerCase().includes(searchText.toLowerCase());

    return matchStatus && matchPriority && matchType && matchSearch;
  });

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div style={{ width: "100%", backgroundColor: "#f5f5f5", padding: "30px 20px", minHeight: "100vh" }}>
      <Notes />
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ padding: "30px", borderRadius: "10px", backgroundColor: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          {/* Header with Back Button */}
          <div style={{ marginBottom: "30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button
                onClick={() => navigate("/inquiryHistory")}
                style={{
                  backgroundColor: "transparent",
                  color: "#d32f2f",
                  border: "1px solid #d32f2f",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                title="Back to Inquiry History"
              >
                <ArrowBackIcon sx={{ fontSize: "18px" }} /> Back
              </button>
              <div>
                <h1 style={{ color: "#d32f2f", fontWeight: "bold", marginBottom: "8px", fontSize: "20px" }}>
                  Inquiry Tickets — #{shippingCalculationId}
                </h1>
                <p style={{ color: "#999", fontSize: "12px" }}>
                  Tickets raised for Inquiry #{shippingCalculationId} | Total: {tickets.length} | Displaying: {paginatedTickets.length}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                fetchTicketsByInquiry();
                tickets.forEach((ticket) => {
                  fetchUnreadCount(ticket.ticketNumber);
                });
              }}
              style={{
                backgroundColor: "#d32f2f",
                color: "white",
                padding: "8px 16px",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
              }}
              title="Refresh tickets and notifications"
            >
              🔄 Refresh
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ width: "40px", height: "40px", border: "3px solid #d32f2f", borderTop: "3px solid transparent", borderRadius: "50%", margin: "0 auto", animation: "spin 1s linear infinite" }} />
              <p style={{ marginTop: "16px", color: "#666", fontSize: "14px" }}>Loading tickets...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ backgroundColor: "#ffebee", border: "1px solid #ef5350", color: "#c62828", padding: "16px", borderRadius: "4px", marginBottom: "16px" }}>
                {error}
              </div>
              <button
                onClick={fetchTicketsByInquiry}
                style={{
                  backgroundColor: "#d32f2f",
                  color: "white",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Filters */}
          {!loading && !error && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "25px", padding: "20px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
              <div>
                <label style={{ color: "#666", fontWeight: "600", display: "block", marginBottom: "6px", fontSize: "12px" }}>SEARCH</label>
                <input
                  type="text"
                  placeholder="Search by ticket, subject, user..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ color: "#666", fontWeight: "600", display: "block", marginBottom: "6px", fontSize: "12px" }}>TYPE</label>
                <select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="all">All Types</option>
                  <option value="inquiry">Inquiry</option>
                  <option value="order">Order Issue</option>
                  <option value="technical">Technical</option>
                  <option value="billing">Billing</option>
                  <option value="feedback">Feedback</option>
                </select>
              </div>

              <div>
                <label style={{ color: "#666", fontWeight: "600", display: "block", marginBottom: "6px", fontSize: "12px" }}>STATUS</label>
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="pending">Pending</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              <div>
                <label style={{ color: "#666", fontWeight: "600", display: "block", marginBottom: "6px", fontSize: "12px" }}>PRIORITY</label>
                <select
                  value={filterPriority}
                  onChange={(e) => {
                    setFilterPriority(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="all">All Priority</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          )}

          {/* Table */}
          {!loading && !error && filteredTickets.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <h3 style={{ color: "#999", marginBottom: "16px", fontSize: "18px" }}>No tickets found for this inquiry</h3>
              <p style={{ color: "#ccc", fontSize: "14px" }}>No tickets have been raised for Inquiry #{shippingCalculationId}</p>
            </div>
          ) : !loading && !error ? (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#d32f2f" }}>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "left" }}>Ticket #</th>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "left" }}>Subject</th>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "left" }}>Customer</th>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "center" }}>Type</th>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "center" }}>Status</th>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "center" }}>Priority</th>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "center" }}>Date</th>
                      <th style={{ color: "white", fontWeight: "600", fontSize: "12px", padding: "12px", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          backgroundColor: "white",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fafafa")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                      >
                        <td style={{ fontWeight: "600", color: "#333", fontSize: "13px", padding: "12px" }}>{ticket.ticketNumber}</td>
                        <td style={{ fontSize: "13px", padding: "12px" }}>
                          <div style={{ fontWeight: "500", color: "#333" }}>{ticket.subject.substring(0, 30)}</div>
                          <div style={{ color: "#999", fontSize: "12px", marginTop: "4px" }}>ID: {ticket.id}</div>
                        </td>
                        <td style={{ fontSize: "13px", padding: "12px" }}>
                          <div style={{ fontWeight: "500", color: "#333" }}>{ticket.userName}</div>
                          <div style={{ color: "#999", fontSize: "12px", marginTop: "4px" }}>{ticket.userEmail}</div>
                        </td>
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <span style={{ backgroundColor: "#e3f2fd", color: "#1976d2", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "500" }}>
                            {getTypeText(ticket.type)}
                          </span>
                        </td>
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <span style={{ backgroundColor: getStatusColor(ticket.status), color: getStatusTextColor(ticket.status), padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "500" }}>
                            {ticket.status}
                          </span>
                        </td>
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <span
                            style={{
                              backgroundColor: getPriorityColor(ticket.priority),
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            {ticket.priority}
                          </span>
                        </td>
                        <td style={{ textAlign: "center", color: "#333", fontSize: "13px", padding: "12px" }}>📅 {formatDate(ticket.date)}</td>
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <button
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setShowDetails(true);
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#1976d2",
                              cursor: "pointer",
                              padding: "4px 8px",
                              fontSize: "18px",
                            }}
                            title="View Details"
                          >
                            👁️
                          </button>
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <button
                              onClick={() => handleOpenChat(ticket)}
                              style={{
                                background: "none",
                                border: "none",
                                color: "#d32f2f",
                                cursor: "pointer",
                                padding: "4px 8px",
                                fontSize: "18px",
                              }}
                              title="Reply"
                            >
                              💬
                            </button>
                            {unreadCounts[ticket.ticketNumber] > 0 && (
                              <span style={{
                                position: "absolute",
                                top: "-2px",
                                right: "0px",
                                backgroundColor: "#d32f2f",
                                color: "white",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: "bold",
                                border: "2px solid white",
                              }}>
                                {unreadCounts[ticket.ticketNumber]}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 0", borderTop: "1px solid #f0f0f0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <label style={{ color: "#666", fontSize: "14px" }}>Results per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: "6px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px",
                      background: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: currentPage === 1 ? "default" : "pointer",
                      opacity: currentPage === 1 ? 0.5 : 1,
                      fontSize: "16px",
                    }}
                  >
                    ◄
                  </button>
                  <span style={{ color: "#666", fontSize: "14px" }}>
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px",
                      background: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: currentPage === totalPages ? "default" : "pointer",
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      fontSize: "16px",
                    }}
                  >
                    ►
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Ticket Details Modal */}
      {showDetails && selectedTicket && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={handleCloseDetailsDialog}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "24px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#d32f2f",
                color: "white",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                Ticket {selectedTicket.ticketNumber}
              </h2>
              <button
                onClick={handleCloseDetailsDialog}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "white",
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: "24px" }}>
              {/* Info Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginBottom: "24px",
                }}
              >
                <div>
                  <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Subject</p>
                  <p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedTicket.subject}</p>
                </div>

                <div>
                  <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Date</p>
                  <p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>📅 {formatDate(selectedTicket.date)}</p>
                </div>

                <div>
                  <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Customer</p>
                  <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedTicket.userName}</p>
                  <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>{selectedTicket.userEmail}</p>
                </div>

                <div>
                  <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Car Model</p>
                  <p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedTicket.carModel}</p>
                </div>
              </div>

              <hr style={{ margin: "24px 0", border: "none", borderTop: "1px solid #f0f0f0" }} />

              {/* Description */}
              <div>
                <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Description</p>
                <p style={{ margin: "0 0 24px 0", fontSize: "14px", color: "#333", lineHeight: "1.6", backgroundColor: "#fafafa", padding: "12px", borderRadius: "4px", border: "1px solid #f0f0f0" }}>
                  {selectedTicket.description}
                </p>
              </div>

              {/* Attachment (if any) */}
              {selectedTicket.attachmentFileName && (
                <div style={{ backgroundColor: "#f0f7ff", border: "1px solid #bbdefb", padding: "12px", borderRadius: "4px", marginBottom: "24px" }}>
                  <p style={{ margin: "0 0 4px 0", fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Attachment</p>
                  <p style={{ margin: 0, fontSize: "13px", color: "#1976d2" }}>📎 {selectedTicket.attachmentFileName}</p>
                </div>
              )}

              {/* Status badges */}
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Status:</span>
                  <span style={{ backgroundColor: getStatusColor(selectedTicket.status), color: getStatusTextColor(selectedTicket.status), padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "500" }}>
                    {selectedTicket.status}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Priority:</span>
                  <span style={{ backgroundColor: getPriorityColor(selectedTicket.priority), color: "white", padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "500" }}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase" }}>Type:</span>
                  <span style={{ backgroundColor: "#e3f2fd", color: "#1976d2", padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "500" }}>
                    {getTypeText(selectedTicket.type)}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                onClick={() => {
                  handleCloseDetailsDialog();
                  handleOpenChat(selectedTicket);
                }}
                style={{
                  backgroundColor: "#d32f2f",
                  color: "white",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                💬 Reply
              </button>
              <button
                onClick={handleCloseDetailsDialog}
                style={{
                  backgroundColor: "white",
                  color: "#666",
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Panel */}
      {showReplyChat && selectedTicket && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
          <div style={{ backgroundColor: "white", borderRadius: "8px", width: "90%", maxWidth: "600px", height: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
            {/* Chat Header */}
            <div style={{ padding: "16px 24px", backgroundColor: "#d32f2f", color: "white", borderRadius: "8px 8px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>{selectedTicket.ticketNumber}</h3>
                <p style={{ fontSize: "12px", color: "#ffebee", margin: "4px 0 0 0" }}>{selectedTicket.subject}</p>
              </div>
              <button
                onClick={handleCloseChat}
                style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: "24px" }}
              >
                ✕
              </button>
            </div>

            {/* Chat Messages */}
            <div style={{ flex: 1, backgroundColor: "#fafafa", padding: "24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "12px", zIndex: "1000" }}>
              {loadingChat && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <div style={{ width: "30px", height: "30px", border: "2px solid #d32f2f", borderTop: "2px solid transparent", borderRadius: "50%", margin: "0 auto", animation: "spin 1s linear infinite" }} />
                  <p style={{ color: "#666", fontSize: "12px", marginTop: "8px" }}>Loading messages...</p>
                </div>
              )}

              {chatError && (
                <div style={{ backgroundColor: "#ffebee", border: "1px solid #ef5350", color: "#c62828", padding: "12px", borderRadius: "4px", fontSize: "12px" }}>
                  {chatError}
                </div>
              )}

              {!loadingChat && chatMessages.length === 0 && !chatError && (
                <div style={{ textAlign: "center", color: "#999", padding: "20px", fontSize: "13px" }}>
                  No messages yet. Start a conversation!
                </div>
              )}

              {chatMessages.map((msg) => (
                <div key={msg.chatId} style={{ display: "flex", justifyContent: msg.senderType === "USER" ? "flex-end" : "flex-start" }}>
                  <div
                    style={{
                      maxWidth: "75%",
                      padding: "12px 16px",
                      backgroundColor: msg.senderType === "USER" ? "#e3f2fd" : getSenderBgColor(msg.senderType),
                      borderRadius: "8px",
                      border: `1px solid ${msg.senderType === "USER" ? "#bbdefb" : "#f0f0f0"}`,
                    }}
                  >
                    <p style={{ fontWeight: "600", fontSize: "12px", margin: 0, color: getSenderColor(msg.senderType) }}>
                      {msg.senderFullName} {msg.senderType !== "USER" && `(${msg.senderType})`}
                    </p>
                    {msg.message && (
                      <p style={{ marginTop: "6px", color: "#333", fontSize: "13px", lineHeight: "1.5", wordBreak: "break-word" }}>
                        {msg.message}
                      </p>
                    )}
                    {msg.attachmentFileName && (
                      <button
                        onClick={() => handleDownloadAttachment(msg.chatId, msg.attachmentFileName)}
                        disabled={isDownloading[msg.chatId]}
                        style={{
                          display: "inline-block",
                          marginTop: "8px",
                          padding: "6px 10px",
                          backgroundColor: "#1976d2",
                          color: "white",
                          textDecoration: "none",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "500",
                          border: "none",
                          cursor: isDownloading[msg.chatId] ? "not-allowed" : "pointer",
                          opacity: isDownloading[msg.chatId] ? 0.6 : 1,
                        }}
                      >
                        📎 {isDownloading[msg.chatId] ? "Downloading..." : msg.attachmentFileName}
                      </button>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "6px" }}>
                      <p style={{ color: "#999", fontSize: "11px", margin: 0 }}>
                        {formatChatTime(msg.formattedDateTime)}
                      </p>
                      {msg.senderType === "USER" && (
                        <div style={{ marginLeft: "8px" }}>
                          {getMessageStatusIndicator(msg)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Message Input */}
            <div style={{ borderTop: "1px solid #f0f0f0", padding: "16px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {attachedFile && (
                <div style={{ fontSize: "12px", color: "#666", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", backgroundColor: "#e3f2fd", borderRadius: "4px" }}>
                  <span>📎 Attached: {attachedFile.name}</span>
                  <button
                    onClick={() => {
                      setAttachedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    style={{ background: "none", border: "none", color: "#d32f2f", cursor: "pointer", fontSize: "16px" }}
                  >
                    ✕
                  </button>
                </div>
              )}

              <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder={attachedFile ? "Add a caption..." : "Type your reply..."}
                  style={{
                    flex: 1,
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "13px",
                    minHeight: "60px",
                    fontFamily: "inherit",
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />

                <label
                  htmlFor="file-upload-inquiry"
                  style={{
                    padding: "8px 12px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "18px",
                    whiteSpace: "nowrap",
                    height: "fit-content",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "background-color 0.2s ease",
                  }}
                  title="Attach file (Max 5MB)"
                >
                  <AttachFileIcon sx={{ fontSize: "20px", color: "#666" }} />
                  <input
                    id="file-upload-inquiry"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </label>

                <button
                  onClick={handleSendReply}
                  disabled={(!replyMessage.trim() && !attachedFile) || sendingMessage}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#d32f2f",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    fontWeight: "600",
                    cursor: "pointer",
                    opacity: (!replyMessage.trim() && !attachedFile) || sendingMessage ? 0.5 : 1,
                    fontSize: "14px",
                  }}
                >
                  {sendingMessage ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showSuccess && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            backgroundColor: successMessage.includes("Failed") ? "#f44336" : "#4caf50",
            color: "white",
            padding: "16px 24px",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontSize: "14px",
          }}
        >
          ✓ {successMessage}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <FooterManual />
    </div>
  );
};

export default InquiryTickets;